import type { Diagnostic } from '@bigint-project/core';

/**
 * App 殼層共用的視圖 / 選取 / 檔案狀態型別。
 * 這裡刻意與 `packages/core` 的 Project 領域模型分開放，
 * 避免各視圖各自加 local state 造成日後打架。
 */

// ---------------------------------------------------------------------------
// 選取模型
// ---------------------------------------------------------------------------

/** 目前作用中的格（任務表格編輯用）。 */
export interface ActiveCell {
  readonly taskId: string;
  readonly columnId: string;
}

// ---------------------------------------------------------------------------
// 視圖狀態
// ---------------------------------------------------------------------------

export type ViewName =
  | 'gantt'
  | 'networkDiagram'
  | 'resourceSheet'
  | 'resourceUsage'
  | 'costTable'
  | 'projectOverview'
  | 'evmReport'
  | 'reports'
  | 'calendar'
  | 'board'
  | 'timesheet';

/**
 * 甘特圖時間刻度。上限為 `'quarter'` —— 刻意不含 `'year'`：
 * `components/gantt/**` 的渲染邏輯（`time-scale.ts`／`graph-geometry.ts`）
 * 目前只支援到季刻度，介面不該提供渲染層做不到的選項（選了 A 卻顯示 B）。
 * 若未來甘特圖加了年刻度支援，這裡才跟著加。
 */
export type TimeScale = 'day' | 'week' | 'month' | 'quarter';

/**
 * 甘特圖依賴線的顯示範圍（與 `components/gantt/types.ts` 的同名型別對齊；
 * 兩邊各自宣告是為了維持「甘特圖是純展示層、不反向依賴 store」的分界）。
 *
 * - `all`：全部畫出（預設，與舊行為一致）
 * - `selected`：只畫與目前選取任務相關的線
 * - `none`：完全不畫，改看表格的「前置任務」欄
 */
export type DependencyLineMode = 'all' | 'selected' | 'none';

export interface ColumnDef {
  readonly id: string;
  /** 對應 Task / Resource 欄位名稱，或自訂欄位 id。 */
  readonly field: string;
  readonly label: string;
  readonly width: number;
  readonly visible: boolean;
}

export const DEFAULT_COLUMNS: readonly ColumnDef[] = [
  { id: 'wbs', field: 'wbs', label: 'WBS', width: 64, visible: true },
  { id: 'name', field: 'name', label: '任務名稱', width: 240, visible: true },
  { id: 'duration', field: 'durationMinutes', label: '工期', width: 80, visible: true },
  { id: 'start', field: 'start', label: '開始', width: 120, visible: true },
  { id: 'finish', field: 'finish', label: '完成', width: 120, visible: true },
  { id: 'predecessors', field: 'predecessors', label: '前置任務', width: 120, visible: true },
  { id: 'resourceNames', field: 'resourceNames', label: '資源', width: 160, visible: true },
];

// ---------------------------------------------------------------------------
// 報表 preset（持久化的 view state，不屬於 core Project）
// ---------------------------------------------------------------------------

export type ReportDataset = 'Tasks' | 'Resources';
export type ReportLayout = 'Table' | 'Chart' | 'Comparison';
export type ReportFilter =
  | 'all'
  | 'active'
  | 'completed'
  | 'late'
  | 'critical'
  | 'milestone'
  | 'overallocated'
  | 'budget';

export type ReportFieldId =
  | 'name'
  | 'wbs'
  | 'start'
  | 'finish'
  | 'duration'
  | 'work'
  | 'percentComplete'
  | 'cost'
  | 'actualCost'
  | 'remainingCost'
  | 'variance'
  | 'isCritical'
  | 'isMilestone'
  | 'type'
  | 'group'
  | 'maxUnits'
  | 'isOverallocated'
  | 'isBudget';

export interface ReportSort {
  readonly field: ReportFieldId;
  readonly direction: 'asc' | 'desc';
}

export interface ReportPreset {
  readonly id: string;
  readonly name: string;
  readonly dataset: ReportDataset;
  readonly layout: ReportLayout;
  readonly fields: readonly ReportFieldId[];
  readonly filter: ReportFilter;
  readonly groupBy: 'none' | 'outlineLevel';
  readonly sorts: readonly ReportSort[];
  readonly outlineLevel: number | null;
  readonly printOrientation: 'portrait' | 'landscape';
}

export type CreateReportPresetInput = Omit<ReportPreset, 'id'> & { readonly id?: string };
export type UpdateReportPresetInput = Partial<Omit<ReportPreset, 'id'>>;

const TASK_REPORT_FIELDS = new Set<ReportFieldId>([
  'name', 'wbs', 'start', 'finish', 'duration', 'work', 'percentComplete', 'cost',
  'actualCost', 'remainingCost', 'variance', 'isCritical', 'isMilestone',
]);
const RESOURCE_REPORT_FIELDS = new Set<ReportFieldId>([
  'name', 'type', 'group', 'maxUnits', 'work', 'cost', 'actualCost', 'remainingCost',
  'isOverallocated', 'isBudget',
]);
const TASK_REPORT_FILTERS = new Set<ReportFilter>([
  'all', 'active', 'completed', 'late', 'critical', 'milestone',
]);
const RESOURCE_REPORT_FILTERS = new Set<ReportFilter>(['all', 'overallocated', 'budget']);
const NUMERIC_REPORT_FIELDS = new Set<ReportFieldId>([
  'duration', 'work', 'percentComplete', 'cost', 'actualCost', 'remainingCost', 'variance', 'maxUnits',
]);

export const DEFAULT_REPORT_PRESETS: readonly ReportPreset[] = [
  {
    id: 'tasks-overview', name: '任務總覽', dataset: 'Tasks', layout: 'Table',
    fields: ['wbs', 'name', 'start', 'finish', 'duration', 'work', 'percentComplete'],
    filter: 'all', groupBy: 'outlineLevel', sorts: [{ field: 'wbs', direction: 'asc' }],
    outlineLevel: null, printOrientation: 'landscape',
  },
  {
    id: 'tasks-critical', name: '要徑任務', dataset: 'Tasks', layout: 'Chart',
    fields: ['name', 'duration', 'work'], filter: 'critical', groupBy: 'none', sorts: [],
    outlineLevel: null, printOrientation: 'landscape',
  },
  {
    id: 'resources-overview', name: '資源總覽', dataset: 'Resources', layout: 'Table',
    fields: ['name', 'type', 'group', 'maxUnits', 'work', 'cost', 'isOverallocated'],
    filter: 'all', groupBy: 'none', sorts: [{ field: 'name', direction: 'asc' }],
    outlineLevel: null, printOrientation: 'landscape',
  },
];

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function isReportField(value: unknown, dataset: ReportDataset): value is ReportFieldId {
  return typeof value === 'string' && (dataset === 'Tasks' ? TASK_REPORT_FIELDS : RESOURCE_REPORT_FIELDS).has(value as ReportFieldId);
}

function fieldsSatisfyLayout(layout: ReportLayout, fields: readonly ReportFieldId[]): boolean {
  if (layout === 'Table') return fields.length > 0;
  const hasLabel = fields.some((field) => !NUMERIC_REPORT_FIELDS.has(field));
  const numericCount = fields.filter((field) => NUMERIC_REPORT_FIELDS.has(field)).length;
  return hasLabel && (layout === 'Chart' ? numericCount >= 1 : numericCount >= 2);
}

/**
 * 將外部（檔案或 future UI）資料收斂為報表契約；不可修復的 layout／欄位組合
 * 回傳 null，呼叫端可安全略過它。欄位和排序一律依 dataset 白名單過濾。
 */
export function sanitizeReportPreset(value: unknown): ReportPreset | null {
  if (!isRecord(value) || typeof value.id !== 'string' || value.id.trim() === '' ||
      typeof value.name !== 'string' || value.name.trim() === '') return null;
  if (value.dataset !== 'Tasks' && value.dataset !== 'Resources') return null;
  const dataset = value.dataset;
  if (value.layout !== 'Table' && value.layout !== 'Chart' && value.layout !== 'Comparison') return null;
  const layout: ReportLayout = value.layout;
  const fields = Array.isArray(value.fields)
    ? value.fields.filter((field): field is ReportFieldId => isReportField(field, dataset))
    : [];
  const uniqueFields = fields.filter((field, index) => fields.indexOf(field) === index);
  if (!fieldsSatisfyLayout(layout, uniqueFields)) return null;

  const allowedFilters = dataset === 'Tasks' ? TASK_REPORT_FILTERS : RESOURCE_REPORT_FILTERS;
  const filter = typeof value.filter === 'string' && allowedFilters.has(value.filter as ReportFilter)
    ? value.filter as ReportFilter
    : 'all';
  const sorts: ReportSort[] = [];
  if (Array.isArray(value.sorts)) {
    for (const sort of value.sorts) {
      if (isRecord(sort) && isReportField(sort.field, dataset) && (sort.direction === 'asc' || sort.direction === 'desc')) {
        sorts.push({ field: sort.field, direction: sort.direction });
      }
    }
  }
  const groupBy = dataset === 'Tasks' && value.groupBy === 'outlineLevel' ? 'outlineLevel' : 'none';
  const outlineLevel = typeof value.outlineLevel === 'number' && Number.isInteger(value.outlineLevel) && value.outlineLevel > 0
    ? value.outlineLevel
    : null;
  const printOrientation = value.printOrientation === 'landscape' ? 'landscape' : 'portrait';
  return {
    id: value.id.trim(), name: value.name.trim(), dataset, layout, fields: uniqueFields, filter,
    groupBy, sorts, outlineLevel, printOrientation,
  };
}

/** Sanitize a persisted list while rejecting duplicate ids/names. `null` means malformed container. */
export function sanitizeReportPresets(value: unknown): readonly ReportPreset[] | null {
  if (!Array.isArray(value)) return null;
  const ids = new Set<string>();
  const names = new Set<string>();
  const presets: ReportPreset[] = [];
  for (const candidate of value) {
    const preset = sanitizeReportPreset(candidate);
    if (preset === null) continue;
    const nameKey = preset.name.toLocaleLowerCase();
    if (ids.has(preset.id) || names.has(nameKey)) continue;
    ids.add(preset.id);
    names.add(nameKey);
    presets.push(preset);
  }
  // 空陣列是使用者刪光 preset 的有效狀態；但非空輸入若沒有任何可用項目，
  // 代表整段資料已損壞，讓 persistence/store 回退到預設值。
  return value.length > 0 && presets.length === 0 ? null : presets;
}

export function cloneDefaultReportPresets(): readonly ReportPreset[] {
  return DEFAULT_REPORT_PRESETS.map((preset) => ({
    ...preset,
    fields: [...preset.fields],
    sorts: preset.sorts.map((sort) => ({ ...sort })),
  }));
}

// ---------------------------------------------------------------------------
// 排程觸發模式
// ---------------------------------------------------------------------------

export type ScheduleMode = 'auto' | 'manual';

export interface ScheduleState {
  readonly mode: ScheduleMode;
  readonly isScheduling: boolean;
  readonly diagnostics: readonly Diagnostic[];
  /** 最近一次排程失敗的訊息（例如引擎尚未實作）；成功時為 null。 */
  readonly lastError: string | null;
  readonly lastRunAt: string | null;
}

// ---------------------------------------------------------------------------
// 檔案狀態
// ---------------------------------------------------------------------------

export interface FileState {
  readonly filePath: string | null;
  readonly isDirty: boolean;
  readonly recentFiles: readonly string[];
  readonly lastSavedAt: string | null;
  readonly isSaving: boolean;
  readonly isLoading: boolean;
  /**
   * `.bproj` 檔案裡目前這版程式不認識的欄位（例如新版程式加的欄位）。
   * 開檔時原樣保留，存檔時原樣寫回，避免舊版程式開一次新版檔案就把
   * 資料洗掉。分別記錄頂層與 `view` 區塊，因為兩者在存檔時是分開組裝的。
   * 見 `src/persistence/serialization.ts`。
   */
  readonly unknownTopLevelFields: Readonly<Record<string, unknown>>;
  readonly unknownViewFields: Readonly<Record<string, unknown>>;
}
